import AsyncStorage from '@react-native-async-storage/async-storage';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';

import api from '@/services/api';
import { Appointment, AvailabilityResult, BookableServiceDetail } from '@/types/appointment';

/**
 * Agendamento público por convidado (guest booking): endpoints sem JWT,
 * escopados pelo slug do prestador.
 *
 * Numa organização `TEAM`, o slug é o do dono e o `professionalId` diz qual
 * profissional da equipe atende; sem ele, é o próprio dono do slug.
 */

/** Query string do profissional escolhido; vazia fora de equipe. */
export function professionalParams(professionalId?: string | null) {
  return professionalId ? { professionalId } : undefined;
}

export const publicScheduleKeys = {
  all: ['public-schedule'] as const,
  serviceDetail: (slug: string, serviceId: string, professionalId: string) =>
    [...publicScheduleKeys.all, 'service-detail', slug, serviceId, professionalId] as const,
  availability: (
    slug: string,
    serviceId: string,
    professionalId: string,
    from: string,
    to: string,
  ) =>
    [...publicScheduleKeys.all, 'availability', slug, serviceId, professionalId, from, to] as const,
  teamAvailability: (slug: string, serviceId: string, from: string, to: string) =>
    [...publicScheduleKeys.all, 'team-availability', slug, serviceId, from, to] as const,
  guestAppointment: (id: string) => [...publicScheduleKeys.all, 'guest-appointment', id] as const,
  myAppointments: (phone: string) => [...publicScheduleKeys.all, 'my-appointments', phone] as const,
};

const OTP_TOKEN_STORAGE_PREFIX = 'guest-otp-token:';
const MAGIC_TOKEN_STORAGE_PREFIX = 'guest-magic-token:';
const GUEST_IDENTITY_STORAGE_KEY = 'guest-identity';

/** Token de telefone verificado, memorizado no device por telefone. */
export async function getStoredOtpToken(phone: string): Promise<string | null> {
  try {
    return await AsyncStorage.getItem(`${OTP_TOKEN_STORAGE_PREFIX}${phone.replace(/\D/g, '')}`);
  } catch {
    return null;
  }
}

export async function storeOtpToken(phone: string, token: string): Promise<void> {
  try {
    await AsyncStorage.setItem(`${OTP_TOKEN_STORAGE_PREFIX}${phone.replace(/\D/g, '')}`, token);
  } catch {
    // Sem storage, o guest só refaz o OTP na próxima vez.
  }
}

/** Token recusado pelo backend (expirado/inválido): não adianta reusar. */
export async function clearStoredOtpToken(phone: string): Promise<void> {
  try {
    await AsyncStorage.removeItem(`${OTP_TOKEN_STORAGE_PREFIX}${phone.replace(/\D/g, '')}`);
  } catch {
    // Sem storage, nada a limpar.
  }
}

/**
 * Link mágico do agendamento, guardado no device. Sem isso, o único caminho de
 * volta ao agendamento é a mensagem do WhatsApp — some junto com ela.
 */
export async function storeMagicToken(appointmentId: string, token: string): Promise<void> {
  try {
    await AsyncStorage.setItem(`${MAGIC_TOKEN_STORAGE_PREFIX}${appointmentId}`, token);
  } catch {
    // Sem storage, o guest volta pelo link do WhatsApp.
  }
}

export async function getStoredMagicToken(appointmentId: string): Promise<string | null> {
  try {
    return await AsyncStorage.getItem(`${MAGIC_TOKEN_STORAGE_PREFIX}${appointmentId}`);
  } catch {
    return null;
  }
}

export interface GuestIdentity {
  name: string;
  phone: string;
}

/**
 * Nome/telefone do último agendamento neste device: pré-preenche o formulário
 * de conta e a próxima marcação, para o guest não redigitar nada.
 */
export async function storeGuestIdentity(identity: GuestIdentity): Promise<void> {
  try {
    await AsyncStorage.setItem(GUEST_IDENTITY_STORAGE_KEY, JSON.stringify(identity));
  } catch {
    // Sem storage, o guest só redigita.
  }
}

export async function getStoredGuestIdentity(): Promise<GuestIdentity | null> {
  try {
    const raw = await AsyncStorage.getItem(GUEST_IDENTITY_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as GuestIdentity) : null;
  } catch {
    return null;
  }
}

const GUEST_ADDRESS_STORAGE_KEY = 'guest-address';

/**
 * Último endereço que o convidado informou num serviço em que o profissional
 * vai até ele — a pergunta "usar o mesmo?" da próxima marcação.
 */
export async function storeGuestAddress(address: string): Promise<void> {
  try {
    await AsyncStorage.setItem(GUEST_ADDRESS_STORAGE_KEY, address);
  } catch {
    // Sem storage, o convidado só redigita.
  }
}

export async function getStoredGuestAddress(): Promise<string | null> {
  try {
    return await AsyncStorage.getItem(GUEST_ADDRESS_STORAGE_KEY);
  } catch {
    return null;
  }
}

export interface GuestSession {
  identity: GuestIdentity;
  otpToken: string;
}

/**
 * Quem é o convidado neste device e o token que prova o telefone dele — é o que
 * dá acesso aos agendamentos sem conta. Null quando o device nunca agendou ou o
 * token de 30 dias já foi embora.
 */
export async function getStoredGuestSession(): Promise<GuestSession | null> {
  const identity = await getStoredGuestIdentity();
  if (!identity?.phone) return null;
  const otpToken = await getStoredOtpToken(identity.phone);
  return otpToken ? { identity, otpToken } : null;
}

export function usePublicServiceDetail(
  slug: string | undefined,
  serviceId: string | undefined,
  professionalId?: string | null,
) {
  return useQuery({
    queryKey: publicScheduleKeys.serviceDetail(slug ?? '', serviceId ?? '', professionalId ?? ''),
    queryFn: async () => {
      const res = await api.get<BookableServiceDetail>(
        `/public/schedule/${slug}/services/${serviceId}`,
        { params: professionalParams(professionalId) },
      );
      return res.data;
    },
    enabled: !!slug && !!serviceId,
    // Trocar de profissional troca de serviço: a tela fica de pé enquanto chega.
    placeholderData: keepPreviousData,
  });
}

export function usePublicAvailability(params: {
  slug?: string;
  serviceId?: string;
  professionalId?: string | null;
  from: string;
  to: string;
}) {
  const { slug, serviceId, professionalId, from, to } = params;
  return useQuery({
    queryKey: publicScheduleKeys.availability(
      slug ?? '',
      serviceId ?? '',
      professionalId ?? '',
      from,
      to,
    ),
    queryFn: async () => {
      const res = await api.get<AvailabilityResult>(`/public/schedule/${slug}/availability`, {
        params: { serviceId, from, to, ...professionalParams(professionalId) },
      });
      return res.data;
    },
    enabled: !!slug && !!serviceId,
  });
}

/**
 * Disponibilidade da equipe inteira: a união dos horários de quem faz o
 * serviço, cada horário dizendo quem está livre nele. É o modo "qualquer
 * profissional" — a pessoa sai junto com o horário, não antes dele.
 */
export function usePublicTeamAvailability(params: {
  slug?: string;
  serviceId?: string;
  from: string;
  to: string;
  enabled?: boolean;
}) {
  const { slug, serviceId, from, to, enabled = true } = params;
  return useQuery({
    queryKey: publicScheduleKeys.teamAvailability(slug ?? '', serviceId ?? '', from, to),
    queryFn: async () => {
      const res = await api.get<AvailabilityResult>(`/public/schedule/${slug}/availability/team`, {
        params: { serviceId, from, to },
      });
      return res.data;
    },
    enabled: enabled && !!slug && !!serviceId,
  });
}

export function useRequestGuestOtp(slug: string | undefined, professionalId?: string | null) {
  return useMutation({
    mutationFn: async (phone: string) => {
      // devCode só vem em dev com PUBLIC_BOOKING_OTP_DEV_ECHO=true.
      const res = await api.post<{ sent: boolean; devCode?: string }>(
        `/public/schedule/${slug}/otp/request`,
        { phone },
        { params: professionalParams(professionalId) },
      );
      return res.data;
    },
  });
}

export function useVerifyGuestOtp(slug: string | undefined) {
  return useMutation({
    mutationFn: async ({ phone, code }: { phone: string; code: string }) => {
      const res = await api.post<{ otpToken: string }>(`/public/schedule/${slug}/otp/verify`, {
        phone,
        code,
      });
      await storeOtpToken(phone, res.data.otpToken);
      return res.data;
    },
  });
}

export interface GuestAppointmentResult {
  appointment: Appointment;
  magicToken: string;
  /** false quando o telefone já pertence a uma conta completa (aí é login). */
  accountClaimable: boolean;
}

export function useCreateGuestAppointment(
  slug: string | undefined,
  professionalId?: string | null,
) {
  return useMutation({
    mutationFn: async (data: {
      serviceId: string;
      startAt: string;
      guest: { name: string; phone: string };
      otpToken?: string;
      address?: string;
    }) => {
      const res = await api.post<GuestAppointmentResult>(
        `/public/schedule/${slug}/appointments`,
        data,
        { params: professionalParams(professionalId) },
      );
      await storeMagicToken(res.data.appointment.id, res.data.magicToken);
      return res.data;
    },
  });
}

/** Pedido de recorrência/assinatura esperando o prestador: sem agendamento ainda. */
export interface GuestPendingRequest {
  id: string;
  serviceName: string;
  price: number;
  startDate: string;
  endDate: string | null;
  isSubscription: boolean;
  requestedAt: string | null;
  slots: {
    frequency: string;
    daysOfWeek: number[];
    dayOfMonth: number | null;
    startTime: string | null;
    endTime: string | null;
    intervalWeeks: number;
  }[];
}

export interface GuestMyAppointmentsResult {
  appointments: { appointment: Appointment; magicToken: string }[];
  /**
   * Recorrências/assinaturas pedidas e ainda não aprovadas. Só materializam
   * quando o prestador aprova — até lá é o único sinal de que o pedido existe.
   */
  pendingRequests?: GuestPendingRequest[];
  /** Telefone já pertence a uma conta com senha: o caminho é login, não OTP. */
  requiresLogin: boolean;
}

/**
 * Agendamentos do convidado pelo telefone verificado. Os links mágicos vêm
 * frescos do servidor e são regravados no device — é o que sobrevive a fechar
 * a aba e a perder a mensagem do WhatsApp.
 */
export function useGuestMyAppointments(session: GuestSession | null) {
  return useQuery({
    queryKey: publicScheduleKeys.myAppointments(session?.identity.phone ?? ''),
    queryFn: async () => {
      const res = await api.post<GuestMyAppointmentsResult>('/public/schedule/my-appointments', {
        otpToken: session!.otpToken,
      });
      await Promise.all(
        res.data.appointments.map(({ appointment, magicToken }) =>
          storeMagicToken(appointment.id, magicToken),
        ),
      );
      return res.data;
    },
    enabled: !!session,
    retry: false,
  });
}

export function useGuestAppointment(appointmentId: string | undefined, token: string | undefined) {
  return useQuery({
    queryKey: publicScheduleKeys.guestAppointment(appointmentId ?? ''),
    queryFn: async () => {
      const res = await api.get<Appointment>(`/public/schedule/appointments/${appointmentId}`, {
        params: { token },
      });
      return res.data;
    },
    enabled: !!appointmentId && !!token,
    retry: false,
  });
}

export function useCancelGuestAppointment(appointmentId: string | undefined) {
  return useMutation({
    mutationFn: async ({ token, reason }: { token: string; reason?: string }) => {
      const res = await api.post<Appointment>(
        `/public/schedule/appointments/${appointmentId}/cancel`,
        { token, reason },
      );
      return res.data;
    },
  });
}
